import { Box, Grid, Typography, Link } from '@mui/material';

import logoText from '@common/assets/images/logo-text.svg';

type Props = {
  links?: { text: string; url: string }[];
  version?: string;
  environment?: string;
};

/**
 * Common site footer.
 *
 * @param props - The component props.
 * @param props.links - Optional footer links to render.
 * @param props.version - The build version string. When present, it is
 *   appended to the copyright line as a `v{version}` suffix. Omit to render
 *   no suffix.
 * @param props.environment - The deploy environment (e.g. `local`,
 *   `production`). The version suffix is hidden when this equals `production`.
 * @returns The rendered footer.
 */
export function Footer({ links, version, environment }: Props) {
  const versionSuffix =
    environment !== 'production' && version ? ` v${version}` : '';

  return (
    <footer>
      <Grid
        container
        data-test-id="footer-container"
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'row',
          height: '152px',
          justifyContent: 'space-between',
          [theme.breakpoints.down('md')]: {
            alignItems: 'center',
            flexDirection: 'column',
            height: 'auto',
            rowGap: '32px',
          },
        })}
      >
        <Grid
          container
          columnSpacing="32px"
          item
          sx={(theme) => ({
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
            paddingLeft: '28px',
            [theme.breakpoints.down('md')]: {
              flexWrap: 'wrap',
              height: 'auto',
              justifyContent: 'center',
              paddingLeft: '0px',
              paddingTop: '24px',
              rowGap: '4px',
            },
            width: 'auto',
          })}
        >
          {links?.map((item) => {
            return (
              <Grid item key={item.text}>
                <Box>
                  <Link
                    href={item.url}
                    sx={(theme) => ({
                      '&:hover': {
                        textDecoration: 'underline',
                        textDecorationColor: theme.colors.grey[600],
                      },
                      color: theme.colors.grey[600],
                      textDecoration: 'none',
                    })}
                  >
                    <Typography
                      variant="smBold"
                      sx={(theme) => ({
                        [theme.breakpoints.down('md')]: {
                          fontSize: '11px',
                          fontWeight: 700,
                        },
                      })}
                    >
                      {item.text}
                    </Typography>
                  </Link>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        <Grid
          container
          item
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            paddingRight: '28px',
            paddingTop: '68px',
            textAlign: 'right',
            [theme.breakpoints.down('md')]: {
              paddingBottom: '24px',
              paddingRight: '0px',
              paddingTop: '0px',
              textAlign: 'center',
            },
            width: 'auto',
          })}
        >
          <Grid item>
            <Box
              sx={(theme) => ({
                height: '28px',
                [theme.breakpoints.down('md')]: {
                  height: '15px',
                },
                width: 'auto',
              })}
              component="img"
              src={logoText}
              alt="Meiji Logo"
            ></Box>
          </Grid>
          <Grid
            item
            sx={(theme) => ({
              [theme.breakpoints.down('md')]: {
                marginTop: '-4px',
              },
            })}
          >
            <Typography
              variant="smBold"
              sx={(theme) => ({
                [theme.breakpoints.down('md')]: {
                  fontSize: '10px',
                  fontWeight: 700,
                },
              })}
            >
              Copyright Meiji Co., Ltd. All Rights Reserved{versionSuffix}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </footer>
  );
}
